import { observer } from "mobx-react-lite";
import React from "react";

import {
  FontIcon,
  Heading,
  Separator,
  Stack,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { When } from "@ui-components/withPerm.tsx";

import { getUserStylesSet } from "../../../../users/components/UserScreens.styles.tsx";
import { useBookingTenantSettingsFormSubmit } from "./BookingTenantSettingsForm.hooks.ts";
import { BookingTenantSettingValues } from "./BookingTenantSettingsForm.types.ts";
import {
  tenantSettingFormDropdown,
  TenantSettingsFormsDropDown
} from "./TenantSettingsFormDropDown.tsx";

const nameOf = nameOfFactory<BookingTenantSettingValues>();

const BookingTenantSettingsFormBase: React.FC = observer(() => {
  const { booking, clinical, core, formDesign, notification } = useStores();

  const theme = useTheme();
  const { formFooter, formFields } = getUserStylesSet(theme);

  const getFormIdFromTypeCode = (code: string) => {
    const design = formDesign.formDesigns?.find(x => x.formTypeCode === code);
    return design?.id;
  };

  const tenantSettings = booking.tenantSettings;
  const clinicalActivityPreference = clinical.clinicalActivitiesPreference;

  const initialValues: Partial<BookingTenantSettingValues> = {
    apptConfirmationEnabled:
      tenantSettings?.appointmentConfirmationSettings?.isActive ?? false,
    demographics: getFormIdFromTypeCode(
      tenantSettingFormDropdown.demographics.typeCode
    ),
    acc: getFormIdFromTypeCode(tenantSettingFormDropdown.acc.typeCode),
    accDemographics: getFormIdFromTypeCode(
      tenantSettingFormDropdown.accDemographics.typeCode
    ),
    notifyUpcomingReminders:
      !!clinicalActivityPreference?.notifyUpcomingReminders
  };

  const onSubmit = useBookingTenantSettingsFormSubmit();

  return (
    <SubmissionForm<BookingTenantSettingValues>
      formName="communications-preferences"
      onSubmitSucceeded={() => {
        notification.success("Communications preference has been updated.");
      }}
      readOnly={!core.hasPermissions(Permission.OrgUnitSettingWrite)}
      onSubmit={onSubmit}
      initialValues={initialValues}
      buttonsProps={{
        disableCancelOnPristine: true,
        styles: {
          root: formFooter
        }
      }}
    >
      {() => {
        return (
          <Stack styles={{ root: formFields }} tokens={{ childrenGap: 8 }}>
            <Stack tokens={{ childrenGap: 16 }}>
              <When permission={Permission.ApptConfirmationsAllowed}>
                <Stack>
                  <Heading labelPaddings>
                    Appointments{" "}
                    <TooltipHost content="After an appointment has been created SMS / email the details, depending on patient preferences">
                      <FontIcon
                        iconName="Info"
                        styles={{ root: { fontSize: 12 } }}
                      />
                    </TooltipHost>
                  </Heading>
                  <CheckboxField
                    name={nameOf("apptConfirmationEnabled")}
                    label="Send appointment confirmation to patient"
                  />
                </Stack>
                <Separator />
              </When>
            </Stack>

            <When permission={Permission.FormDesignWrite}>
              <Stack tokens={{ childrenGap: 12 }}>
                <Heading variant="section-heading-light">
                  New patient & new claim forms{" "}
                  <TooltipHost content="Optionally send forms when creating a new appointment or new claim">
                    <FontIcon
                      iconName="info"
                      styles={{ root: { fontSize: 12 } }}
                    />
                  </TooltipHost>
                </Heading>
                <TenantSettingsFormsDropDown formKey="demographics" />
                <TenantSettingsFormsDropDown formKey="acc" />
                <TenantSettingsFormsDropDown formKey="accDemographics" />
              </Stack>
            </When>

            <When permission={Permission.ClinicalReminderPreferenceAllowed}>
              <Separator />
              <Stack tokens={{ childrenGap: 12 }}>
                <Heading variant="section-heading-light">
                  Clinical reminders
                </Heading>
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                  <CheckboxField
                    label="Notify me of next week's reminders"
                    name={nameOf("notifyUpcomingReminders")}
                  />
                  <TooltipHost content="Show in the notifications list">
                    <FontIcon
                      iconName="info"
                      styles={{ root: { fontSize: 12 } }}
                    />
                  </TooltipHost>
                </Stack>
              </Stack>
            </When>
          </Stack>
        );
      }}
    </SubmissionForm>
  );
});

export const BookingTenantSettingsForm = withFetch(async ({ formDesign }) => {
  return {
    formDesigns: await formDesign.getFormDesigns()
  };
}, BookingTenantSettingsFormBase);
